import { useRef, type PointerEvent } from 'react';
import { Section, SectionHeading } from '~/components/layout';
import { SmartImage } from '~/components/ui';
import type { CompareStep } from '~/content/compare';

/**
 * Shared "3 easy steps" section: a titled row of step cards
 * (image + title + description). Used by both /send-money-home and /compare so
 * the markup — including the 20px (gap-5) title→description gap — stays in one
 * place and identical across pages.
 *
 * Mobile: a horizontal, drag/swipe slider (mirrors the "Built for people just
 * like you" grid — same gap-5 track, fixed card width, hidden scrollbar) so the
 * next card peeks at the edge. Desktop (md+): the original 3-column grid, unchanged.
 */
export function StepsSection({ heading, steps }: { heading: string; steps: CompareStep[] }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const drag = useRef({ down: false, startX: 0, startLeft: 0 });

  // Mouse click-and-drag horizontal scroll on the mobile track — mirrors the
  // TestimonialGrid carousel. Native touch-swipe/momentum still works because we
  // only adjust scrollLeft; on desktop the row has no horizontal overflow so
  // these handlers are inert.
  const onPointerDown = (e: PointerEvent) => {
    const el = trackRef.current;
    if (!el) return;
    drag.current = { down: true, startX: e.clientX, startLeft: el.scrollLeft };
    el.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent) => {
    const el = trackRef.current;
    if (!el || !drag.current.down) return;
    el.scrollLeft = drag.current.startLeft - (e.clientX - drag.current.startX);
  };

  const endDrag = (e: PointerEvent) => {
    drag.current.down = false;
    trackRef.current?.releasePointerCapture?.(e.pointerId);
  };

  return (
    <Section>
      <SectionHeading title={heading} />
      <div
        ref={trackRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDragStart={(e) => e.preventDefault()}
        className="mt-4 flex cursor-grab gap-5 overflow-x-auto select-none [-ms-overflow-style:none] [scrollbar-width:none] active:cursor-grabbing [&::-webkit-scrollbar]:hidden md:grid md:cursor-default md:grid-cols-9 md:gap-x-5 md:gap-y-10 md:overflow-visible md:active:cursor-default">
        {steps.map((step) => (
          <article
            key={step.title}
            className="flex w-[292px] shrink-0 flex-col gap-8 md:col-span-3 md:w-auto">
            <SmartImage image={step.image} className="aspect-square w-full" />
            <div className="flex flex-col gap-5">
              <h3 className="text-h5 font-semibold text-ink">{step.title}</h3>
              <p className="text-body-m text-grey-900">{step.description}</p>
            </div>
          </article>
        ))}
      </div>
    </Section>
  );
}
